import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, msalReady, loginRequest, apiTokenRequest } from '../auth/msalConfig';
import { setAuthToken } from './api';
import { ROLE_ADMIN, isAdminUser } from './permissions';

// Entra-based admin auth (Authorization Code + PKCE redirect) against the corporate workforce
// tenant. Replaces the previous basic-auth flow against /api/v1/auth/*. The waive-admin app role
// (assigned in the workforce tenant) is the authorization gate — enforced here for UX and by the
// backend's require_admin dependency. See docs/adr/0001-identity-and-access-model.md.

const ADMIN_ROLE = ROLE_ADMIN;

// Decode a JWT payload (base64url) without verifying — used only to read the `roles` claim from
// the access token for the client-side UX gate. The backend independently validates the token.
function decodeJwt(token) {
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(decodeURIComponent(escape(atob(payload))));
    } catch {
        return {};
    }
}

// App roles reliably appear in the ACCESS token (audience = the waive-services API), not always in
// the ID token — so authorize off the access token, which is also what the backend enforces.
function rolesFromToken(token) {
    const claims = decodeJwt(token);
    return Array.isArray(claims.roles) ? claims.roles : [];
}

function userFromAccount(account, roles) {
    const claims = account?.idTokenClaims || {};
    return {
        name: account?.name || claims.name || account?.username,
        username: account?.username || claims.preferred_username,
        email: claims.email || account?.username,
        role: roles.includes(ADMIN_ROLE) ? ADMIN_ROLE : (roles[0] || 'user'),
        roles,
    };
}

function activeAccount() {
    return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
}

// Acquire a waive-services access token for the given account, falling back to an interactive
// redirect if silent acquisition needs user interaction (consent, expired refresh, etc.).
async function acquireApiToken(account) {
    try {
        const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
        return result.accessToken;
    } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect(apiTokenRequest);
            return null; // navigates away
        }
        throw e;
    }
}

export const authService = {
    // Kicks off the hosted-login redirect; the browser navigates away and returns to the SPA
    // origin, where validateSession() completes the flow.
    async login() {
        await msalReady;
        await msalInstance.loginRedirect(loginRequest);
    },

    async logout() {
        await msalReady;
        setAuthToken(null);
        const account = activeAccount();
        await msalInstance.logoutRedirect({ account });
    },

    // Called once on app load. Completes any pending redirect, acquires the waive-services access
    // token, and authorizes off its `roles` claim. A signed-in user WITHOUT the waive-admin role
    // is shown the login screen with a denial message (their MSAL session is left intact — no
    // half-authenticated state, no invalid API calls).
    async validateSession() {
        await msalReady;
        const redirectResult = await msalInstance.handleRedirectPromise();
        if (redirectResult?.account) {
            msalInstance.setActiveAccount(redirectResult.account);
        }

        const account = activeAccount();
        if (!account) return { valid: false };
        msalInstance.setActiveAccount(account);

        let token;
        try {
            token = await acquireApiToken(account);
        } catch (e) {
            return { valid: false, message: 'Could not acquire an access token — please sign in again.' };
        }
        if (!token) return { valid: false }; // redirecting to re-acquire interactively

        const roles = rolesFromToken(token);
        // Any admin role admits you to the app; admin-api enforces what you can actually DO
        // (ISO 27001 A.5.3). Gating on waive-admin alone would lock out everyone holding only a
        // narrow role — e.g. an auditor with waive-reader + waive-member-reader — which would make
        // the segregated role model unusable.
        if (!isAdminUser(roles)) {
            setAuthToken(null);
            return { valid: false, message: 'Access denied — a Waive admin role is required.' };
        }

        setAuthToken(token);
        return { valid: true, user: userFromAccount(account, roles) };
    },

    getUser() {
        const account = activeAccount();
        if (!account) return null;
        const roles = Array.isArray(account.idTokenClaims?.roles) ? account.idTokenClaims.roles : [];
        return userFromAccount(account, roles);
    },

    async getToken() {
        await msalReady;
        const account = activeAccount();
        return account ? acquireApiToken(account) : null;
    },

    isLoggedIn() {
        return !!activeAccount();
    },
};

export default authService;
